import { useEffect, useState, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Slab } from 'react-loading-indicators';
import { Context } from '../../../Context';
import CustomToolTip from '../../ToolTipInfoCustomMUI';
import Zoom from '@mui/material/Zoom';
import Select from 'react-select';
import { ReactComponent as IconSale } from '../../../media/img/icon-sale.svg';
import styles from '../../../styles/bim.module.scss';
import apart_styles from '../../../styles/aprt.module.scss';
import { AVAILABILITY_STATUS } from '../../../constants/availabilityStatus';

const { AVAILABLE, SOLD, RESERVED, UNKNOWN } = AVAILABILITY_STATUS;

function ApartmentInfoBlock({ apartmentData, showPrice = false }) {
	const {
		dataUrl,
		setActiveApartmentHandle,
		favoriteApartmentUUIDs,
		toggleFavoriteApartmentHandler,
		tiersData,
		buildingsData,
		activeApartmentData,
	} = useContext(Context);
	const [svgContent, setSvgContent] = useState(null);
	const [isHeartHovered, setIsHeartHovered] = useState(false);
	const svgRef = useRef(null);
	const { t } = useTranslation();

	const isMultipleEdifice = buildingsData.length !== 1;
	const isMultipleGates = true; //TODO: implement once gates are added
	const apartmentEdificeName = activeApartmentData.edifice_name;
	const apartmentTier = activeApartmentData.ref_tier;
	const apartmentTierName = tiersData?.find(
		(tier) =>
			tier.edifice === apartmentEdificeName && tier.ref_tier === apartmentTier
	)?.name;

	const isCurrentUuidInFavorites = favoriteApartmentUUIDs.includes(
		activeApartmentData.uuid
	);

	
	const parkingSpacesOptions = [
		{ value: 0, label: 'bez miejsca' },
		{ value: 1, label: 'jedno miejsce' },
		{ value: 2, label: 'dwa miejsca' },
	];

	const [selectedParkingSpaces, setSelectedParkingSpaces] = useState([]);
	const storageUnitOptions = [
		{ value: 0, label: 'bez komórki' },
		{ value: 1, label: 'jedna komórka' },
		{ value: 2, label: 'dwie komórki' },
	];

	const [selectedStorageUnits, setSelectedStorageUnits] = useState([]);

	const customStyles = {
		control: (provided) => ({
			...provided,
			borderRadius: '0',
		}),
	};

	const availabilityLabel = (apartment) => {
		switch (apartment.availability) {
			case SOLD:
				return <span className="state-sold">{t('sold')}</span>;
			case RESERVED:
				return <span className="state-reserved">{t('reserved')}</span>;
			case AVAILABLE:
				return <span className="state-available">{t('available')}</span>;
			case UNKNOWN:
				return <span>{t('no_data')}</span>;
			default:
				return <span>{t('no_data')}</span>;
		}
	};

	const formatArea = (num) => {
		if (num) {
			const roundedNumber = num.toFixed(2);
			const localeString = (number) =>
				number.toLocaleString('pl-PL', {
					minimumFractionDigits: 2,
				});

			return localeString(+roundedNumber);
		}
		return '';
	};

	const formatExpositionName = (direction) => {
		switch (direction) {
			case 'N':
				return t('north_short');
			case 'E':
				return t('east_short');
			case 'S':
				return t('south_short');
			case 'W':
				return t('west_short');
			case 'NE':
				return t('northeast_short');
			case 'NW':
				return t('northwest_short');
			case 'SE':
				return t('southeast_short');
			case 'SW':
				return t('southwest_short');
			default:
				return direction;
		}
	};

	const renderExposition = (expositionArray) => {
		if (!expositionArray || expositionArray.length === 0) {
			return t('lack_of');
		}
		const formattedDirections = expositionArray.map((dir) =>
			formatExpositionName(dir)
		);
		return formattedDirections.join(', ');
	};

	const convertNumberToHHMM = (num) => {
		const milliseconds = num * 60 * 60 * 1000;
		const timeString = new Date(milliseconds).toISOString().slice(11, 16);
		let [hours, minutes] = timeString.split(':');
		hours = parseInt(hours, 10);

		return `${hours}:${minutes}`;
	};

	const convertArrayToHoursRange = (arr) => {
		if (!arr || arr.length === 0) {
			return t('lack_of');
		}
		const ranges = arr.map((range) => {
			const [start, end] = range;
			return `${convertNumberToHHMM(start)} - ${convertNumberToHHMM(end)}`;
		});
		return ranges.join(', ');
	};

	const sumSunLightRanges = (arr) => {
		if (!arr || arr.length === 0) return 0;
		let sum = 0;
		arr.forEach((range) => {
			sum += range[1] - range[0];
		});
		return sum;
	};

	return (
		<div className={apart_styles.infoList_block}>
			<div className={apart_styles.apartment_info_availability_container}>
				{/* <div className={apart_styles.apartment_info_flags_element}>
          <IconSale style={{ width: '1.5rem', height: '1.5rem' }} />
          <IconSale style={{ width: '1.5rem', height: '1.5rem' }} />
          <IconSale style={{ width: '1.5rem', height: '1.5rem' }} />
          <IconSale style={{ width: '1.5rem', height: '1.5rem' }} />
				</div> */}
				<div className={apart_styles.apartment_info_data_element}>
					<span className={apart_styles.apartment_info_data_value}>
						{availabilityLabel(activeApartmentData)}
					</span>
				</div>
			</div>
			{(isMultipleEdifice || isMultipleGates) && (
				<>
					<div className={apart_styles.apartment_info_data_container}>
						{isMultipleEdifice && (
							<div className={apart_styles.apartment_info_data_element}>
								<span className={apart_styles.apartment_info_data_type}>
									{t('building')}
								</span>
								<span className={apart_styles.apartment_info_data_value}>
									{activeApartmentData.edifice_name}
								</span>
							</div>
						)}
						{isMultipleGates && (
							<div className={apart_styles.apartment_info_data_element}>
								<span className={apart_styles.apartment_info_data_type}>
									{t('gate')}
								</span>
								<span className={apart_styles.apartment_info_data_value}>
									{activeApartmentData.gate}
								</span>
							</div>
						)}
					</div>
				</>
			)}
			<div className={apart_styles.apartment_info_data_container}>
				<div className={apart_styles.apartment_info_data_element}>
					<span className={apart_styles.apartment_info_data_type}>{t('tier')}</span>
					<span className={apart_styles.apartment_info_data_value}>
						{apartmentTierName}
					</span>
				</div>
				<div className={apart_styles.apartment_info_data_element}>
					<span className={apart_styles.apartment_info_data_type}>{t('rooms')}</span>
					<span className={apart_styles.apartment_info_data_value}>
						{activeApartmentData.rooms}
					</span>
				</div>
				<div className={apart_styles.apartment_info_data_element}>
					<span className={apart_styles.apartment_info_data_type}>{t('area')}</span>
					<span className={apart_styles.apartment_info_data_value}>
						{formatArea(activeApartmentData.area_original)} m<sup>2</sup>
					</span>
				</div>
			</div>
			<div className={apart_styles.apartment_info_data_container}>
								<div className={apart_styles.apartment_info_data_element}>
					<span className={apart_styles.apartment_info_data_type}>
						{t('insolation')}
						<CustomToolTip
							title={
								<>
									<p className={apart_styles.infoTooltip__paragraph}>
										{t('insolation_info_1')}
									</p>
									<p className={apart_styles.infoTooltip__disclaimer}>
										{t('calculation_info')}
									</p>
								</>
							}
							slots={{
								transition: Zoom,
							}}
							arrow
						>
							<button className={apart_styles.infoToolTip__button}>?</button>
						</CustomToolTip>
					</span>
					<span className={apart_styles.apartment_info_data_value}>
						{convertNumberToHHMM(
							sumSunLightRanges(activeApartmentData.sun_light_ranges)
						)}
						{` h`}
					</span>
				</div>
				<div className={apart_styles.apartment_info_data_element}>
					<span className={apart_styles.apartment_info_data_type}>
						{t('exposition')}
						<CustomToolTip
							title={
								<>
									<p className={apart_styles.infoTooltip__paragraph}>
										{t('exposition_info_1')}
									</p>
									<p className={apart_styles.infoTooltip__paragraph}>
										{t('exposition_info_2')}
									</p>
								</>
							}
							slots={{
								transition: Zoom,
							}}
							arrow
						>
							<button className={apart_styles.infoToolTip__button}>?</button>
						</CustomToolTip>
					</span>
					<span className={apart_styles.apartment_info_data_value}>
						{renderExposition(activeApartmentData.exposition)}
					</span>
				</div>
			</div>
						{/* <div
							style={{ marginTop: '0.5rem' }}
						>
							<p
								style={{ margin: '0.5rem 0', color: 'var(--color-headings)' }}
							>
								Dodaj do zapytania:
							</p>
							<div style={{ display: 'flex', gap: '0.5rem', jkustifyContent: 'space-between' }}>
								<div
									className={`${styles.filter_container} ${styles.filter_buttons}`}
									style={{ flexGrow: 1 }}
								>
									<Select
										styles={customStyles}
										value={parkingSpacesOptions.filter((opt) =>
											selectedParkingSpaces.includes(opt.value)
										)}
										options={parkingSpacesOptions}
										onChange={(selectedOptions) => {
											const selected = selectedOptions
												? selectedOptions.map((opt) => opt.value)
												: [];
											setSelectedParkingSpaces(selected);
										}}
										className={styles.multiselect}
										placeholder={`m. postojowe`}
									/>
								</div>
								<div
									className={`${styles.filter_container} ${styles.filter_buttons}`}
								>
									<Select
										value={storageUnitOptions.filter((opt) =>
											selectedStorageUnits.includes(opt.value)
										)}
										options={storageUnitOptions}
										onChange={(selectedOptions) => {
											const selected = selectedOptions
												? selectedOptions.map((opt) => opt.value)
												: [];
											setSelectedStorageUnits(selected);
										}}
										className={styles.multiselect}
										placeholder={`komórki lok.`}
									/>
								</div>
							</div>
						</div> */}
		</div>
	);
}

export default ApartmentInfoBlock;
